import type {
  GeminiRequestPayload,
  WireAdherence,
  WireConditionBand,
  WireDomain,
  WireDomainGoal,
  WireEvidence,
  WireEvidenceState,
  WireIntention,
  WireMeasurements,
  WirePhaseContext,
  WirePhaseType,
  WireRatePosition,
  WireSummaries,
  WireTrendDirection,
  WireTrends,
} from '~/contracts/gemini/request'
import { GEMINI_CONTRACT_VERSION } from '~/contracts/gemini/version'
import type { DomainGoalRecord } from '~/models/fitnessDomain'
import type { Confidence } from '~/models/rulesOpinion'
import type { DerivedFacts, PhaseFacts } from '~/rules/facts'
import type { AdherenceEvidence } from '~/rules/prioritize'

/**
 * Turning what Project Sola knows into what it is allowed to say (D-093 §2).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS READS SUMMARISED FACTS, NEVER RECORDS
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  The input is `DerivedFacts` — what `rules/` already computed for the week —
 *  plus the domain goals. **It is never the stored records themselves**, and the
 *  distinction is the whole safety argument rather than a matter of taste.
 *
 *  A `DomainGoalRecord` carries `notes`, `reassessWhen` (free text the user
 *  wrote), `startedOn`, `endedOn`, `id`, `projectId` and a change history with
 *  timestamps. Every one of those is forbidden on the wire. So this file reads
 *  named fields off it and **never spreads it**: `{...goal}` typechecks against
 *  a wider structural target far more often than anyone expects, and a single
 *  careless spread is precisely what `findForbiddenContent` was written to
 *  catch. Reading four fields by name cannot leak a fifth.
 *
 *  `DerivedFacts` is already the purified form — the rules engine is pure and
 *  fixture-testable, so it has no access to a photo, a note or a name in the
 *  first place. Building from it means the payload starts from a value that
 *  structurally cannot contain most of what is forbidden, and this file only has
 *  to drop the dates.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  A MISSING VALUE IS NEVER A ZERO, ON THE WIRE TOO
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Every optional number here is omitted when absent rather than defaulted. A
 *  week with no qualifying weigh-in has no weight change, not a change of zero,
 *  and the two read identically to a model while meaning opposite things — the
 *  project rule applied at the one boundary where the reader cannot ask a
 *  follow-up question.
 *
 *  The same goes for `recordedSessions`: absent means nothing was recorded,
 *  which for a domain whose evidence Sola does not yet collect is honest, and
 *  zero would be an accusation.
 */

/**
 * What the builder needs, gathered by the caller.
 *
 * A plain value rather than a database handle, so this stays pure and testable
 * without a fixture database — the same split `collectRulesInput` and
 * `evaluateWeek` already use, for the same reason.
 */
export interface PayloadSources {
  readonly facts: DerivedFacts
  readonly adherence: AdherenceEvidence
  readonly confidence: Confidence
  /** Active domain goals only. An inactive domain is absent, never `inactive`. */
  readonly domainGoals: readonly DomainGoalRecord[]
  readonly constraints: {
    readonly sessionsAvailablePerWeek?: number
    readonly minutesPerSession?: number
    readonly hasGymAccess?: boolean
  }
  /**
   * The active phase type, which `DerivedFacts` does not carry.
   *
   * The engine dispatches on it before deriving anything, so the facts describe
   * a phase without naming its kind. **Absent when body composition is inactive**
   * — there is no phase then (D-090), and a cardio-only user has no phase block
   * at all rather than an empty one.
   */
  readonly phaseType?: WirePhaseType
  /**
   * The D-071 condition band, supplied rather than derived here.
   *
   * It comes from waist-to-height, and height lives on the profile, which
   * `DerivedFacts` deliberately does not carry — the engine has no rule that
   * reads a height directly. Computing it here would put a second
   * implementation of the 0.50 boundary in the codebase, and the only published
   * threshold in this project is not something to have two of. The caller reads
   * the profile and passes the answer; `'unknown'` is the honest value when
   * there is no waist or no height, and is not a default standing in for one.
   */
  readonly conditionBand: WireConditionBand
  /**
   * The four opt-in values, present only when the user chose to include them on
   * this request (D-093 §2). Never remembered between requests.
   */
  readonly measurements?: WireMeasurements
}

/**
 * `unknown` is a blank field and never a poor week.
 *
 * The engine's own distinction, carried onto the wire so a reviewer cannot read
 * a step the user has not reached as a failure to follow the plan. `high` and
 * `sufficientToJudge` both mean the plan was actually tested, and the wire
 * vocabulary has one word for that.
 */
function wireAdherence(adherence: AdherenceEvidence): WireAdherence {
  switch (adherence) {
    case 'high':
      return 'good'
    case 'sufficientToJudge':
      return 'mixed'
    case 'tooLowToJudge':
      return 'tooLowToJudge'
    case 'unknown':
      return 'unknown'
  }
}

/**
 * Direction of travel from a signed change, with a dead band for noise.
 *
 * **Never a magnitude ordering.** The `ratePosition` lesson in its third
 * costume: a reader given a number without a direction will infer one, and for
 * a maintenance range straddling zero the inference is wrong half the time.
 */
function directionFrom(change: number | undefined, flat: boolean | undefined): WireTrendDirection {
  if (change === undefined) return 'unknown'
  if (flat === true) return 'steady'
  if (change > 0) return 'rising'
  if (change < 0) return 'falling'
  return 'steady'
}

/**
 * Where the week's rate sits against the phase's own range.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  READS THE ENGINE'S ANSWER. NEVER RECOMPUTES IT
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  The first version of this function compared the week's change against the
 *  range bounds itself. It was four lines, it looked obviously right, and it was
 *  the `ratePosition`/`rangeSide` defect for the third time in this project:
 *  **`ratePosition` needs to know which way the phase is trying to go**, and its
 *  sign heuristic reads any range with a negative lower bound as a losing phase.
 *  Maintenance runs −0.25% to +0.25%, so a naive comparison classifies a gain as
 *  "slower than range" — which is how a 0.6% *gain* once read as underperforming.
 *
 *  So maintenance uses `rangeSide` (plain above/inside/below) and everything else
 *  uses `ratePosition`, exactly as the rulesets do. Both are already computed;
 *  recomputing either would put a second implementation in the codebase whose
 *  disagreement with the first would be visible only to a third party.
 */
function wireRatePosition(phase: PhaseFacts, type: WirePhaseType): WireRatePosition {
  if (type === 'maintenance') {
    switch (phase.rangeSide) {
      case 'above':
        return 'slower'
      case 'below':
        return 'faster'
      case 'inside':
        return 'inRange'
      default:
        return 'unknown'
    }
  }

  switch (phase.ratePosition) {
    case 'fasterThanRange':
      return 'faster'
    case 'slowerThanRange':
      return 'slower'
    case 'insideRange':
      return 'inRange'
    default:
      return 'unknown'
  }
}

/**
 * The phase block.
 *
 * The phase *type* is not on `DerivedFacts` — the engine dispatches on it before
 * deriving anything, so the facts describe a phase without naming which kind it
 * is. The caller passes it, which also means this file never has to guess.
 */
function wirePhase(
  facts: DerivedFacts,
  type: WirePhaseType | undefined,
): WirePhaseContext | undefined {
  const phase = facts.phase
  if (phase === undefined || type === undefined) return undefined

  return {
    type,
    /* Week N, never a calendar date (D-060's `dayIndex` reasoning). Ordering and
       elapsed time survive; the date does not, so nothing here pins a real-world
       day the user could be identified by. */
    weekIndex: phase.weekNumber,
    targetRateLowPercent: phase.targetMinPercent,
    targetRateHighPercent: phase.targetMaxPercent,
    ratePosition: wireRatePosition(phase, type),
  }
}

/**
 * The waist change as a percentage of where it started.
 *
 * The wire field is a percentage and `WaistFacts` holds centimetres, so the
 * conversion happens here rather than a raw measurement being sent under a name
 * that says percent. The baseline is the previous reading — `latest - change` —
 * because a change expressed against the *new* value would shrink as the waist
 * shrinks and read as a slowing that never happened.
 *
 * Returns `undefined` rather than zero when there is nothing to divide by. A
 * missing value is never a zero, and a zero here would say "your waist has not
 * moved" to someone who has measured it once.
 */
function waistChangePercent(facts: DerivedFacts): number | undefined {
  const change = facts.waist.changeCm
  const latest = facts.waist.latestCm
  if (change === undefined || latest === undefined) return undefined

  const previous = latest - change
  if (previous <= 0) return undefined

  return (change / previous) * 100
}

function wireTrends(facts: DerivedFacts, band: WireConditionBand): WireTrends {
  const waistPercent = waistChangePercent(facts)

  return {
    weightDirection: directionFrom(facts.weight.weeklyChangePercent, facts.weight.isFlat),
    ...(facts.weight.weeklyChangePercent === undefined
      ? {}
      : { weightChangePercent: facts.weight.weeklyChangePercent }),
    /* The waist has no flatness flag of its own, so nothing is passed for it
       rather than inventing a threshold here. A dead band chosen in this file
       would be a coaching number nobody approved — `nutritionSourceConflict`'s
       refusal of a tolerance, applied to a different measurement. */
    waistDirection: directionFrom(facts.waist.changeCm, undefined),
    ...(waistPercent === undefined ? {} : { waistChangePercent: waistPercent }),
    conditionBand: band,
  }
}

function wireSummaries(facts: DerivedFacts, adherence: AdherenceEvidence): WireSummaries {
  const recovery = facts.recovery.meanScore

  return {
    adherence: wireAdherence(adherence),
    ...(recovery === undefined ? {} : { recoveryScore: recovery }),
    /* Strength is sent as a direction rather than as the counts the engine uses.
       The counts are how a rule fires; a reviewer needs to know whether strength
       is holding, and the number of lifts below a threshold invites arithmetic
       nobody asked for. */
    ...(facts.strength.unavailable
      ? {}
      : {
          strengthTrend:
            facts.strength.liftsDecliningThisWeek > 0 ? ('falling' as const) : ('steady' as const),
        }),
  }
}

/**
 * One wire goal per active domain, read field by field.
 *
 * **`plannedSessionsPerWeek` comes from the standard week**, which is the plan
 * the user confirmed. The short and minimum-viable variants are deliberately not
 * sent: they exist so a bad week has a floor rather than a failure, and handing
 * a reviewer three versions of a week invites it to comment on which one was
 * used — which is the judging of a low-energy day that the whole three-tier
 * design exists to prevent.
 */
function wireDomainGoals(goals: readonly DomainGoalRecord[]): readonly WireDomainGoal[] {
  const sent: WireDomainGoal[] = []

  for (const goal of goals) {
    /* `inactive` is unrepresentable on the wire: an inactive domain is absent,
       never described as inactive. Sending one would assert a choice the user
       never made, exactly as D-092's backfill refuses to write an `inactive`
       row. */
    if (goal.intention === 'inactive') continue
    if (goal.status !== 'active') continue

    const planned = goal.weeklyCommitment?.standard.reduce(
      (total, item) => total + item.timesPerWeek,
      0,
    )

    sent.push({
      domain: goal.domain as WireDomain,
      intention: goal.intention as WireIntention,
      primary: goal.priority === 'primary',
      ...(planned === undefined ? {} : { plannedSessionsPerWeek: planned }),
    })
  }

  return sent
}

function wireEvidence(facts: DerivedFacts, confidence: Confidence): WireEvidence {
  const weighIns: WireEvidenceState =
    facts.weight.currentSufficiency === 'sufficient'
      ? 'sufficient'
      : facts.weight.entryCount > 0
        ? 'partial'
        : 'missing'

  return {
    weighIns,
    measurements: facts.waist.available ? 'sufficient' : 'missing',
    /* Training and nutrition evidence are reported as `missing` rather than
       guessed at. `sessionDone` is written by nothing as of C6.5, so claiming
       `partial` would be Sola asserting evidence it does not collect — D-060's
       converse at the transmission boundary. */
    training: facts.strength.unavailable ? 'missing' : 'sufficient',
    nutrition: facts.nutrition.authority === 'none' ? 'missing' : 'partial',
    confidence,
  }
}

/**
 * Build the payload.
 *
 * Returns a plain value; **it does not serialize**. `prepareGeminiRequest` is
 * the only path from a payload to bytes and runs the forbidden-content guard on
 * the way through, which is the single-gate property `serializeWeeklyReport`
 * has for exports. Building and serializing are kept apart so the guard cannot
 * be skipped by a caller that already has a payload in hand.
 */
export function buildGeminiPayload(sources: PayloadSources): GeminiRequestPayload {
  const phase = wirePhase(sources.facts, sources.phaseType)

  return {
    contractVersion: GEMINI_CONTRACT_VERSION,
    domains: wireDomainGoals(sources.domainGoals),
    ...(phase === undefined ? {} : { phase }),
    trends: wireTrends(sources.facts, sources.conditionBand),
    summaries: wireSummaries(sources.facts, sources.adherence),
    constraints: sources.constraints,
    evidence: wireEvidence(sources.facts, sources.confidence),
    ...(sources.measurements === undefined ? {} : { measurements: sources.measurements }),
    disclosureProfile: sources.measurements === undefined ? 'default' : 'withMeasurements',
  }
}
